// src/components/TopNav/TopNav.js
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button, Input, Space, Avatar, Badge, Dropdown, Typography } from "antd";
import {
  MenuUnfoldOutlined,
  MenuFoldOutlined,
  BellOutlined,
  MailOutlined,
  SettingOutlined,
  DownOutlined,
  SearchOutlined,
  GlobalOutlined,
  SunOutlined,
  MoonOutlined,
} from "@ant-design/icons";
import { useAuth } from "../../context/AuthContext";
import "./TopNav1.css";

const { Text } = Typography;

const asPositiveInt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
};

const TopNav = ({ toggleSidebar, isSidebarOpen, toggleDarkMode, isDarkMode }) => {
  const { authData, handleLogout } = useAuth();
  const navigate = useNavigate();

  // --- derive user info ----------------------------------------------------
  const displayName = useMemo(() => {
    if (authData?.firstName && authData?.lastName) return `${authData.firstName} ${authData.lastName}`;
    if (authData?.firstName) return authData.firstName;
    if (authData?.userName) return authData.userName;
    return "Lane Sands";
  }, [authData]);

  const primaryRole = useMemo(() => {
    if (Array.isArray(authData?.role) && authData.role.length) return authData.role[0];
    if (typeof authData?.role === "string") return authData.role;
    return "User";
  }, [authData]);

  const initials = useMemo(() => {
    const parts = displayName.trim().split(/\s+/);
    const a = parts[0]?.[0] ?? "";
    const b = parts.length > 1 ? parts[parts.length - 1][0] ?? "" : "";
    return (a + b).toUpperCase() || "LS";
  }, [displayName]);

  const avatarSrc = useMemo(() => {
    const seed = authData?.email || displayName || "lane-sands-user";
    return `https://i.pravatar.cc/40?u=${encodeURIComponent(seed)}`;
  }, [authData?.email, displayName]);

  // --- mail badge: choose one mode -----------------------------------------
  // If you already have a count in authData, map it here.
  // Example fields: authData.mailUnreadCount / authData.outboxUnreadCount / etc.
  const mailCount = useMemo(() => {
    return asPositiveInt(authData?.mailUnreadCount); // change field when your API provides it
  }, [authData?.mailUnreadCount]);

  // MODE A: dot bubble (like bell)
  // - If you want it ALWAYS visible for now, set true.
  // - If you want it only when there are unread items, use: mailCount > 0
  const showMailDot = true; // or: mailCount > 0

  // MODE B: numeric count (1..9+). If count is 0, it hides by default.
  // const showMailCount = mailCount > 0;

  // --- dropdown menu actions ----------------------------------------------
  const onUserMenuClick = ({ key }) => {
    if (key === "profile") return navigate("/hotels/profilesettingspage");
    if (key === "settings") return navigate("/hotel/my/settings");
    if (key === "signout") return handleLogout();
  };

  const userMenu = [
    { key: "profile", label: "Profile" },
    { key: "settings", label: "Settings" },
    { type: "divider" },
    { key: "signout", danger: true, label: "Sign out" },
  ];

  // --- render --------------------------------------------------------------
  return (
    <header className="app-header">
      <div className="topbar">
        {/* LEFT */}
        <div className="topbar-left">
          <Button
            type="text"
            shape="circle"
            className="icon-pill"
            onClick={toggleSidebar}
            icon={isSidebarOpen ? <MenuFoldOutlined /> : <MenuUnfoldOutlined />}
            aria-label="Toggle sidebar"
          />

          <Input className="search" placeholder="Type text..." allowClear prefix={<SearchOutlined />} />
        </div>

        {/* RIGHT */}
        <div className="topbar-right">
          <Space size="middle">
            {/* Language */}
            <Button type="text" shape="circle" className="icon-pill" icon={<GlobalOutlined />} />

            {/* Mail (red bubble like bell) */}
            <Badge
              dot={showMailDot}
              // If you switch to numeric counts later, replace dot with:
              // count={mailCount}
              // overflowCount={9}
              // showZero={false}
              offset={[-2, 2]}
            >
              <Button
                type="text"
                shape="circle"
                className="icon-pill"
                icon={<MailOutlined />}
                onClick={() => navigate("/hotel/emails")}
                aria-label="Outbox logs"
              />
            </Badge>

            {/* Notifications (dot) */}
            <Badge dot offset={[-2, 2]}>
              <Button type="text" shape="circle" className="icon-pill" icon={<BellOutlined />} aria-label="Notifications" />
            </Badge>

            {/* Theme toggle */}
            {toggleDarkMode && (
              <Button
                type="text"
                shape="circle"
                className="icon-pill"
                onClick={toggleDarkMode}
                icon={isDarkMode ? <MoonOutlined /> : <SunOutlined />}
                aria-label="Toggle theme"
              />
            )}

            {/* User chip */}
            <Dropdown menu={{ items: userMenu, onClick: onUserMenuClick }} trigger={["click"]}>
              <div className="user-chip" role="button" tabIndex={0}>
                <Avatar size="small" src={avatarSrc}>
                  {initials}
                </Avatar>
                <div className="user-meta">
                  <Text type="secondary" className="role">
                    {primaryRole}
                  </Text>
                  <Text className="name">{displayName}</Text>
                </div>
                <DownOutlined className="caret" />
              </div>
            </Dropdown>

            {/* Settings pill */}
             
            <Button
              type="text"
              shape="circle"
              className="icon-pill"
              icon={<SettingOutlined />}
              aria-label="Settings"
              onClick={() => navigate("hotel/my/settings")}
            />

          </Space>
        </div>
      </div>
    </header>
  );
};

export default TopNav;
